/**
 * Client for the standalone API (`obsessless-api`) that is taking over from the Firebase
 * callables. Port of the app's `shared/services/apiClient.ts`, trimmed to what db-admin
 * needs: no backend-call instrumentation (this app has none).
 *
 * Requests carry the Firebase ID token the API verifies, and keep the callable wire format
 * (`{ data }` in, `{ result }` out, `{ error: { message, status } }` on failure), so
 * migrating a function is a one-line change at the call site:
 *
 *   const regenerate = createApiCall<{ userId: string }, Response>('adminGenerateOcdCycleSteps');
 *
 * Admin-only routes are gated on the `role: 'admin'` custom claim server-side, the same
 * claim the callables checked, so nothing extra is sent from here.
 */
import { auth } from '../firebase';

const LOCAL_API_BASE_URL = 'http://localhost:8080';

/**
 * `VITE_USE_LOCAL_API=true` points at an API running on this machine, the counterpart of
 * `VITE_USE_EMULATOR` for the callables.
 */
export const API_BASE_URL =
  import.meta.env.VITE_USE_LOCAL_API === 'true'
    ? LOCAL_API_BASE_URL
    : import.meta.env.VITE_API_BASE_URL ?? LOCAL_API_BASE_URL;

interface ApiErrorBody {
  error?: { message?: string; status?: string };
}

/**
 * Failure reported by the API.
 *
 * `code` is the hyphenated form the callables use (`permission-denied`), so error branches
 * work either side of the migration.
 */
export class ApiError extends Error {
  // Fields rather than constructor parameter properties: this app compiles with
  // `erasableSyntaxOnly`, which rejects the shorthand the app's copy uses.
  readonly code: string;
  readonly httpStatus: number;

  constructor(code: string, message: string, httpStatus: number) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.httpStatus = httpStatus;
  }
}

/** `PERMISSION_DENIED` (the callable wire status) -> `permission-denied`. */
const toErrorCode = (status: string | undefined): string =>
  status ? status.toLowerCase().replace(/_/g, '-') : 'unknown';

export interface ApiCallOptions {
  /** Client-side deadline. Always set this above what the handler is allowed to take. */
  timeoutMs?: number;
}

/**
 * POSTs to `path` on the API and resolves to the unwrapped `result`.
 *
 * @throws ApiError if the API rejects the call, or nobody is signed in.
 */
export const callApi = async <RequestType, ResponseType>(
  path: string,
  data?: RequestType,
  timeoutMs?: number
): Promise<ResponseType> => {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError('unauthenticated', `API ${path} requires a signed-in user`, 401);
  }
  const token = await user.getIdToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ data }),
    ...(timeoutMs !== undefined && { signal: AbortSignal.timeout(timeoutMs) }),
  });

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiError(
      toErrorCode(body.error?.status),
      body.error?.message ?? `API ${path} failed with ${response.status}`,
      response.status
    );
  }

  const { result } = (await response.json()) as { result: ResponseType };
  return result;
};

/**
 * Result of a streaming call: chunks as they arrive, and the final value.
 *
 * Deliberately the same shape `httpsCallable(...).stream()` returns, so a migrated
 * streaming function's caller keeps its `for await` loop and its `await data`.
 */
export interface ApiStream<ChunkType, ResponseType> {
  stream: AsyncIterable<ChunkType>;
  data: Promise<ResponseType>;
}

/** `data: {json}` — the one line format the callable SSE protocol uses. */
const SSE_LINE = /^data: (.*)$/;

export interface StreamApiOptions {
  /**
   * Aborts the request. The one addition to the app's copy of this function: the API turns
   * a client hang-up into an AbortSignal its streaming handler honours, so aborting the
   * fetch is how a long-running route is told to stop — there is no stop endpoint to call.
   */
  signal?: AbortSignal;
}

/**
 * POSTs to `path` and reads the callable streaming protocol back: SSE frames carrying
 * `{ message }` per chunk, one `{ result }` at the end, or `{ error }` if the handler
 * failed partway through.
 *
 * An error arriving mid-stream is why `data` is a promise rather than a return value:
 * the HTTP status was already spent on 200, so the failure surfaces when the caller
 * awaits the result (and from the iterator), not from this call.
 *
 * @throws ApiError before streaming starts — not signed in, or the API rejected the
 *         request outright (auth, bad input).
 */
export const streamApi = async <RequestType, ChunkType, ResponseType>(
  path: string,
  data?: RequestType,
  options?: StreamApiOptions
): Promise<ApiStream<ChunkType, ResponseType>> => {
  const user = auth.currentUser;
  if (!user) {
    throw new ApiError('unauthenticated', `API ${path} requires a signed-in user`, 401);
  }
  const token = await user.getIdToken();

  const response = await fetch(`${API_BASE_URL}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      // What tells the API to stream; without it the same route answers in one piece.
      Accept: 'text/event-stream',
    },
    body: JSON.stringify({ data }),
    ...(options?.signal && { signal: options.signal }),
  });

  if (!response.ok || !response.body) {
    const body = (await response.json().catch(() => ({}))) as ApiErrorBody;
    throw new ApiError(
      toErrorCode(body.error?.status),
      body.error?.message ?? `API ${path} failed with ${response.status}`,
      response.status
    );
  }

  let resolveResult: (value: ResponseType) => void;
  let rejectResult: (reason: ApiError) => void;
  const resultPromise = new Promise<ResponseType>((resolve, reject) => {
    resolveResult = resolve;
    rejectResult = reject;
  });
  // Nothing awaits this until the iterator has run, and an unhandled rejection in
  // between would surface as a console error the caller never sees.
  resultPromise.catch(() => undefined);

  const body = response.body;

  async function* readStream(): AsyncGenerator<ChunkType> {
    const reader = body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let sawResult = false;

    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Frames are newline-delimited; the last piece may be half a line, so it stays
        // in the buffer until the rest of it arrives.
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';

        for (const line of lines) {
          const match = SSE_LINE.exec(line.trim());
          if (!match) continue; // blank separators and comments

          const frame = JSON.parse(match[1]) as {
            message?: ChunkType;
            result?: ResponseType;
            error?: { message?: string; status?: string };
          };

          if (frame.error) {
            const error = new ApiError(toErrorCode(frame.error.status), frame.error.message ?? `API ${path} failed`, 200);
            rejectResult(error);
            throw error;
          }
          if ('result' in frame) {
            sawResult = true;
            resolveResult(frame.result as ResponseType);
            continue;
          }
          if ('message' in frame) {
            yield frame.message as ChunkType;
          }
        }
      }

      if (!sawResult) {
        rejectResult(new ApiError('internal', `API ${path} closed before returning a result`, 200));
      }
    } finally {
      reader.releaseLock();
    }
  }

  return { stream: readStream(), data: resultPromise };
};

/**
 * Creates a typed call to a migrated function.
 *
 * @param name - The function name, which is also its route.
 */
export const createApiCall = <RequestType, ResponseType>(name: string, options?: ApiCallOptions) => {
  return (data: RequestType): Promise<ResponseType> =>
    callApi<RequestType, ResponseType>(`/${name}`, data, options?.timeoutMs);
};
